import { useEffect, useRef, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { EmailPreviewButton } from "@/components/EmailPreviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { normalizePhoneE164 } from "@/lib/utils";
import { TranslateControls } from "@/components/TranslateControls";
import {
  Loader2, Sparkles, Send, Save, Paperclip, X, FolderOpen, Mail, PenLine, Wand2, MessageCircle,
} from "lucide-react";

const CHUNK = 4 * 1024 * 1024; // 4MB

export async function uploadAttachment(file) {
  const { data: initData } = await api.post("/uploads/init");
  const uploadId = initData.upload_id;
  const total = file.size;
  for (let start = 0; start < total; start += CHUNK) {
    const blob = file.slice(start, Math.min(start + CHUNK, total));
    await api.put(`/uploads/${uploadId}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } });
  }
  const { data } = await api.post("/attachments/finalize", {
    upload_id: uploadId, filename: file.name, content_type: file.type || "",
  });
  return data.attachment;
}

const TONES = [
  { value: "professionale", label: "Professionale" },
  { value: "amichevole", label: "Amichevole" },
  { value: "diretto", label: "Diretto e conciso" },
];

/**
 * Unified email composer used by Sales Agent, Media & PR and Ambassador (email).
 * Editable To/CC/BCC, write-from-scratch toggle, AI generation, attachments
 * (upload + Catalogo & Materiali), preview, save-draft / send.
 */
export function EmailComposer({
  open, onOpenChange, target, contact, defaultTo = "",
  allowDraft = true, allowGenerate = true, title = "Componi email",
  channelHint, showPitchType = false, onSent,
  allowWhatsapp = false, waDefaults = {}, waTemplates = [], templateModule = null, initialChannel = "email",
}) {
  const [templates, setTemplates] = useState([]);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [fromScratch, setFromScratch] = useState(false);
  const [tone, setTone] = useState("professionale");
  const [news, setNews] = useState("");
  const [pitchType, setPitchType] = useState("email");
  const [attachments, setAttachments] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [channel, setChannel] = useState("email");
  const [waPhone, setWaPhone] = useState("");
  const [waName, setWaName] = useState("");
  const [waLink, setWaLink] = useState("");
  const [waSending, setWaSending] = useState(false);
  const [waDoc, setWaDoc] = useState(null);
  const [pickerMode, setPickerMode] = useState("attachment");
  const [waTemplateId, setWaTemplateId] = useState("none");
  const [waTemplateName, setWaTemplateName] = useState("brema_outreach");
  const [waPreviewTpl, setWaPreviewTpl] = useState("");
  const [waLang, setWaLang] = useState("auto");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTo(defaultTo || ""); setCc(""); setBcc(""); setShowCcBcc(false);
      setSubject(""); setBody(""); setFromScratch(false); setTone("professionale");
      setNews(""); setPitchType("email"); setAttachments([]);
      setChannel(allowWhatsapp && initialChannel === "whatsapp" ? "whatsapp" : "email");
      setWaPhone(waDefaults.phone || "");
      setWaName(waDefaults.name || contact?.name || "");
      setWaLink(waDefaults.link || "");
      setWaDoc(null); setPickerMode("attachment");
      setWaTemplateId("none"); setWaPreviewTpl(""); setWaTemplateName(waDefaults.template || "brema_outreach");
      setWaLang("auto");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTo]);

  useEffect(() => {
    if (!open) return;
    const q = templateModule ? `?module=${templateModule}` : "";
    api.get(`/send-templates${q}`).then(({ data }) => setTemplates(data.templates || [])).catch(() => setTemplates([]));
  }, [open, templateModule]);

  const applyComposerTemplate = (id) => {
    const t = templates.find((x) => x._id === id);
    if (!t) return;
    setFromScratch(true);
    setSubject(t.subject || "");
    setBody(t.body || "");
    if (Array.isArray(t.attachments) && t.attachments.length) {
      setAttachments((p) => {
        const seen = new Set(p.map((a) => a.storage_path));
        return [...p, ...t.attachments.filter((a) => a.storage_path && !seen.has(a.storage_path))];
      });
    }
    toast.success(`Modello caricato${t.attachments?.length ? " con allegato" : ""}`);
  };

  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const { data } = await api.get("/marketing-assets");
      setAssets((data.assets || []).filter((a) => a.storage_path || a.file_path));
    } catch (e) {
      toast.error("Impossibile caricare il Catalogo & Materiali");
    } finally { setAssetsLoading(false); }
  };
  const openAssetPicker = (mode = "attachment") => { setPickerMode(mode); setAssetPickerOpen(true); if (!assets.length) loadAssets(); };

  const assetName = (a) => a.original_filename || a.title || a.name || a.filename || "File";
  const addAsset = (a) => {
    const sp = a.storage_path || a.file_path;
    if (pickerMode === "wadoc") {
      setWaDoc({ link: fileUrl(sp), filename: assetName(a) });
      toast.success(`Catalogo allegato: ${assetName(a)}`);
      setAssetPickerOpen(false);
      return;
    }
    const ref = {
      storage_path: sp,
      filename: assetName(a),
      content_type: a.content_type || "",
      size: 0,
      size_label: typeof a.size === "string" ? a.size : "",
    };
    if (attachments.some((x) => x.storage_path === sp)) {
      toast.info("Allegato già aggiunto"); return;
    }
    setAttachments((p) => [...p, ref]);
    toast.success(`Allegato: ${ref.filename}`);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of files) added.push(await uploadAttachment(f));
      setAttachments((p) => [...p, ...added]);
      toast.success(`${added.length} allegato/i caricato/i`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload allegato fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const removeAtt = (sp) => setAttachments((p) => p.filter((a) => a.storage_path !== sp));

  const generate = async () => {
    if (!contact?._id) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/outreach/generate", {
        target, contact_id: contact._id, tone, language: "auto",
        pitch_type: pitchType, news,
      });
      setSubject(data.subject || "");
      setBody(data.body || "");
      toast.success("Bozza generata dall'AI · rivedi e personalizza");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const submit = async (action) => {
    if (!to.trim()) { toast.error("Inserisci almeno un destinatario in 'A:'"); return; }
    if (!body.trim()) { toast.error("Il corpo dell'email è vuoto"); return; }
    action === "draft" ? setSavingDraft(true) : setSending(true);
    try {
      const { data } = await api.post("/outreach/send", {
        target, contact_id: contact._id, to, cc, bcc, subject, body,
        pitch_type: pitchType, attachments, action,
      });
      toast.success(data.note || (action === "draft" ? "Bozza salvata" : "Email inviata"));
      onOpenChange(false);
      onSent && onSent(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { action === "draft" ? setSavingDraft(false) : setSending(false); }
  };

  const toggleScratch = () => {
    const next = !fromScratch;
    setFromScratch(next);
    if (next) { setSubject(""); setBody(""); }
  };

  const renderWaPreview = (tpl, name, link) => {
    const n = (name || "Rivenditore").trim() || "Rivenditore";
    const l = (link || waDefaults.link || "https://www.brema-1969.com").trim();
    if (tpl && tpl.trim()) return tpl.replace(/{nome}/g, n).replace(/{link}/g, l);
    return `Ciao ${n}! Siamo Brema Bike, brand italiano di abbigliamento tecnico premium per ciclismo e motociclismo. Vorremmo proporti la nostra collezione come rivenditore. Scopri il catalogo qui: ${l}`;
  };
  const waPreview = renderWaPreview(waPreviewTpl, waName, waLink);

  const waNation = contact?.nation || waDefaults.nation || "Italia";

  // Polling dello stato di consegna reale (aggiornato via webhook Meta statuses).
  const pollDelivery = async (campaignId, tries = 6, intervalMs = 2000) => {
    if (!campaignId) return { delivered: false, failed: false };
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      try {
        const { data } = await api.get(`/campaigns/${campaignId}/delivery`);
        const ds = data.delivery_status || "";
        if (data.status === "errore" || ds === "rimbalzata" || ds === "fallita") {
          return { failed: true, reason: data.wa_error || "rifiutato da WhatsApp/Meta" };
        }
        if (ds === "consegnata" || ds === "aperta") return { delivered: true };
      } catch { /* ignore transient */ }
    }
    return { delivered: false, failed: false };
  };

  const sendWhatsapp = async () => {
    if (!contact?._id) return;
    const normalized = normalizePhoneE164(waPhone, waNation);
    if (!normalized) {
      toast.error("Numero WhatsApp non valido: correggi il numero (es. +39 348 1234567)");
      return;
    }
    if (normalized !== waPhone) setWaPhone(normalized);
    setWaSending(true);
    try {
      const { data } = await api.post("/outreach/send", {
        target, contact_id: contact._id, channel: "whatsapp",
        to: normalized, wa_params: [waName, waLink || waDefaults.link || ""],
        wa_document: waDoc, wa_template: waTemplateName, wa_preview: waPreviewTpl,
        wa_lang: waLang === "auto" ? "" : waLang, action: "send",
      });
      // Nessun invio reale → NIENTE verde: errore rosso col motivo.
      if (!data.sent) {
        toast.error(
          data.note ||
          (data.whatsapp_enabled === false
            ? "WhatsApp non configurato: il messaggio NON è stato inviato."
            : "Messaggio NON inviato: verifica il numero WhatsApp."),
          { duration: 8000 }
        );
        setWaSending(false);
        return;
      }
      // Meta ha accettato → verifica la consegna reale prima di dichiarare successo.
      toast.loading("Inviato a WhatsApp · verifica consegna in corso…", { id: "wa-deliver" });
      const res = await pollDelivery(data.campaign_id);
      if (res.failed) {
        toast.error(`Messaggio NON consegnato a ${normalized}: ${res.reason}`, {
          id: "wa-deliver", duration: 10000,
        });
        setWaSending(false);
        return; // mantieni il dialog aperto per correggere/riprovare
      }
      if (res.delivered) {
        toast.success(`Messaggio WhatsApp consegnato a ${normalized}`, { id: "wa-deliver" });
      } else {
        toast.success(
          `Inviato a ${normalized} · in consegna. Controlla lo stato in Messaggi.`,
          { id: "wa-deliver", duration: 7000 }
        );
      }
      onOpenChange(false);
      onSent && onSent(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setWaSending(false); }
  };

  const busy = generating || sending || savingDraft || waSending;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="email-composer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {channel === "whatsapp" ? <MessageCircle className="h-5 w-5 text-[#25D366]" /> : <Mail className="h-5 w-5 text-brand" />} {title}
            </DialogTitle>
            {channelHint && <DialogDescription>{channelHint}</DialogDescription>}
          </DialogHeader>

          {allowWhatsapp && (
            <div className="mb-1 flex gap-2 rounded-lg border border-border bg-brema-bg/50 p-1" data-testid="composer-channel-toggle">
              <button type="button" data-testid="composer-channel-email" onClick={() => setChannel("email")}
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${channel === "email" ? "bg-selected text-selected-foreground" : "text-foreground hover:bg-brema-hover"}`}>
                <Mail className="mr-1.5 inline h-3.5 w-3.5" /> E-mail (Resend)
              </button>
              <button type="button" data-testid="composer-channel-whatsapp" onClick={() => setChannel("whatsapp")}
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${channel === "whatsapp" ? "bg-[#25D366] text-white" : "text-foreground hover:bg-brema-hover"}`}>
                <MessageCircle className="mr-1.5 inline h-3.5 w-3.5" /> WhatsApp Business
              </button>
            </div>
          )}

          {channel === "whatsapp" ? (
            <div className="space-y-3" data-testid="composer-whatsapp-panel">
              <div className="rounded-lg border border-[#25D366]/40 bg-[#25D366]/10 p-3 text-xs text-foreground">
                <p className="font-semibold">Primo contatto via WhatsApp</p>
                <p className="mt-1 text-muted-foreground">
                  Meta richiede un <strong>template pre-approvato</strong> per il primo messaggio. Verrà inviato il
                  template «{waTemplateName}» con i parametri qui sotto: l'<strong>anteprima</strong> mostra esattamente il testo che partirà.
                  Le risposte {target === "ambassador" ? "dell'ambassador" : "del rivenditore"} arriveranno nella
                  sezione Messaggi, dove potrai proseguire la conversazione a testo libero.
                </p>
                {target === "ambassador" && waTemplateId === "none" && (
                  <p className="mt-1 text-amber-700" data-testid="composer-wa-ambassador-default-note">
                    Il template standard «{waTemplateName}» ha un testo pensato per i rivenditori: per gli ambassador scegli (o crea nel Template Center) un modello WhatsApp dedicato, con il nome del template Meta approvato.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Carica da Archivio Modelli (Template Center · WhatsApp)</Label>
                <Select value={waTemplateId} onValueChange={(v) => {
                  if (v === "none") { setWaTemplateId("none"); setWaPreviewTpl(""); setWaTemplateName(waDefaults.template || "brema_outreach"); return; }
                  const t = waTemplates.find((x) => x._id === v);
                  if (t) { setWaTemplateId(v); setWaPreviewTpl(t.preview_text || ""); setWaTemplateName(t.meta_template_name || "brema_outreach"); if (t.default_link && !waLink) setWaLink(t.default_link); }
                }}>
                  <SelectTrigger data-testid="composer-wa-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Standard (predefinito «{waDefaults.template || "brema_outreach"}»)</SelectItem>
                    {waTemplates.map((t) => <SelectItem key={t._id} value={t._id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                {waTemplates.length === 0 && (
                  <p className="text-[11px] text-muted-foreground" data-testid="composer-wa-no-templates">Nessun modello WhatsApp nell'Archivio: creane uno nel Template Center (canale WhatsApp) per averlo qui.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Numero WhatsApp (formato internazionale)</Label>
                <Input data-testid="composer-wa-phone" value={waPhone}
                  onChange={(e) => setWaPhone(e.target.value)}
                  onBlur={() => { const n = normalizePhoneE164(waPhone, waNation); if (n) setWaPhone(n); }}
                  placeholder="+39 333 1234567" />
                <p className="text-[11px] text-muted-foreground">Formattazione automatica in E.164 (es. +393481234567). Puoi incollarlo in qualsiasi formato.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Lingua template</Label>
                <Select value={waLang} onValueChange={setWaLang}>
                  <SelectTrigger data-testid="composer-wa-lang"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatica (in base al paese)</SelectItem>
                    <SelectItem value="it">Italiano (it)</SelectItem>
                    <SelectItem value="fr">Français (fr)</SelectItem>
                    <SelectItem value="es">Español (es)</SelectItem>
                    <SelectItem value="de">Deutsch (de)</SelectItem>
                    <SelectItem value="en">English (en)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{target === "ambassador" ? "{{1}} Nome ambassador" : "{{1}} Nome rivenditore"}</Label>
                  <Input data-testid="composer-wa-name" value={waName} onChange={(e) => setWaName(e.target.value)} placeholder={target === "ambassador" ? "Nome" : "Nome negozio"} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{"{{2}} Link catalogo"}</Label>
                  <Input data-testid="composer-wa-link" value={waLink} onChange={(e) => setWaLink(e.target.value)} placeholder="https://www.brema-1969.com" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Anteprima messaggio</Label>
                <div data-testid="composer-wa-preview" className="whitespace-pre-line rounded-lg border border-border bg-brema-bg p-3 text-sm text-foreground">{waPreview}</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Allegato catalogo (opzionale · PDF)</Label>
                {waDoc ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-brema-bg px-3 py-2 text-sm" data-testid="composer-wa-doc">
                    <span className="flex min-w-0 items-center gap-2 text-foreground"><Paperclip className="h-4 w-4 shrink-0 text-[#25D366]" /> <span className="truncate">{waDoc.filename}</span></span>
                    <button type="button" onClick={() => setWaDoc(null)} data-testid="composer-wa-doc-remove" className="shrink-0 text-muted-foreground hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <Button type="button" variant="secondary" onClick={() => openAssetPicker("wadoc")} data-testid="composer-wa-doc-btn"
                    className="w-full rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                    <FolderOpen className="mr-2 h-4 w-4" /> Allega catalogo dal Catalogo &amp; Materiali
                  </Button>
                )}
                <p className="text-[11px] text-muted-foreground">Nota: il template Meta deve avere un header di tipo Documento per recapitare l'allegato.</p>
              </div>
              <div className="flex justify-end gap-2 border-t border-border pt-3">
                <Button type="button" onClick={sendWhatsapp} disabled={waSending || !waPhone.trim()} data-testid="composer-wa-send-btn"
                  className="rounded-full bg-[#25D366] text-sm font-semibold text-white hover:bg-[#1FB855] disabled:opacity-40">
                  {waSending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Send className="mr-2 h-4 w-4" /> Invia su WhatsApp</>}
                </Button>
              </div>
            </div>
          ) : (
          <div className="space-y-3">
            {/* Recipients */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">A: (destinatario — separa più email con la virgola)</Label>
              <Input data-testid="composer-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="cliente@email.com, secondo@email.com" />
            </div>
            {!showCcBcc ? (
              <button type="button" data-testid="composer-toggle-ccbcc" onClick={() => setShowCcBcc(true)}
                className="text-xs font-medium text-brand hover:underline">+ Aggiungi CC / CCN</button>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">CC (copia)</Label>
                  <Input data-testid="composer-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="collega@brema.it" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">CCN (copia nascosta)</Label>
                  <Input data-testid="composer-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="archivio@brema.it" />
                </div>
              </div>
            )}

            {/* AI vs scratch controls */}
            {allowGenerate && (
              <div className="rounded-lg border border-border bg-brema-bg/50 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground">
                    {fromScratch ? "Modalità: scrivi da zero" : "Modalità: genera con AI"}
                  </span>
                  <button type="button" data-testid="composer-toggle-scratch" onClick={toggleScratch}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">
                    {fromScratch ? (<><Wand2 className="h-3.5 w-3.5" /> Usa l'AI</>) : (<><PenLine className="h-3.5 w-3.5" /> ✍️ Scrivi da zero</>)}
                  </button>
                </div>
                {!fromScratch && (
                  <div className="space-y-2">
                    {showPitchType && (
                      <Select value={pitchType} onValueChange={setPitchType}>
                        <SelectTrigger data-testid="composer-pitch-type" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email di pitch</SelectItem>
                          <SelectItem value="press_release">Comunicato stampa</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {target === "media" && (
                      <Input data-testid="composer-news" value={news} onChange={(e) => setNews(e.target.value)}
                        placeholder="Novità da comunicare (es. nuova collezione gravel 2026)" className="h-9 text-xs" />
                    )}
                    {target === "leads" && (
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger data-testid="composer-tone" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    <Button type="button" onClick={generate} disabled={generating} data-testid="composer-generate-btn"
                      className="w-full rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                      {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generazione…</> : <><Sparkles className="mr-2 h-4 w-4" /> Genera con AI</>}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Subject + body */}
            {channel === "email" && templates.length > 0 && (
              <div className="space-y-1.5" data-testid="composer-template-block">
                <Label className="text-xs text-muted-foreground">Carica da Archivio Modelli (Moduli AI inclusi)</Label>
                <Select onValueChange={applyComposerTemplate}>
                  <SelectTrigger data-testid="composer-template-select"><SelectValue placeholder="Scegli un modello…" /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t._id} value={t._id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Oggetto Email:</Label>
              <Input data-testid="composer-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto dell'email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Testo email (gli URL diventano link cliccabili)</Label>
              <Textarea data-testid="composer-body" value={body} onChange={(e) => setBody(e.target.value)}
                className="min-h-[180px] text-sm" placeholder="Scrivi o incolla qui il testo… es. Scopri il catalogo su www.brema-1969.com" />
              <TranslateControls text={body} subject={subject} nation={contact?.nation || waDefaults.nation}
                onApply={({ text, subject: s }) => { setBody(text); if (s !== undefined) setSubject(s); }}
                testidPrefix="composer-translate" label="Traduci oggetto e testo" />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Allegati</Label>
              <div className="flex flex-wrap gap-2">
                <input ref={fileRef} type="file" multiple hidden onChange={handleFiles} data-testid="composer-file-input" />
                <Button type="button" variant="secondary" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}
                  data-testid="composer-upload-btn"
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {uploading ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Caricamento…</> : <><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Aggiungi allegato</>}
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={openAssetPicker}
                  data-testid="composer-catalog-btn"
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  <FolderOpen className="mr-1.5 h-3.5 w-3.5" /> Catalogo &amp; Materiali
                </Button>
              </div>
              {attachments.length > 0 && (
                <ul className="space-y-1" data-testid="composer-attachments">
                  {attachments.map((a) => (
                    <li key={a.storage_path} data-testid="composer-att-item"
                      className="flex items-center justify-between gap-2 rounded-lg bg-brema-bg px-3 py-1.5 text-xs">
                      <a href={fileUrl(a.storage_path)} target="_blank" rel="noopener noreferrer"
                        className="flex min-w-0 items-center gap-1.5 truncate text-foreground hover:text-brand hover:underline">
                        <Paperclip className="h-3 w-3 shrink-0 text-brand" />
                        <span className="truncate">{a.filename}</span>
                        {a.size_label && <span className="shrink-0 text-muted-foreground">· {a.size_label}</span>}
                      </a>
                      <button type="button" onClick={() => removeAtt(a.storage_path)} data-testid="composer-att-remove"
                        className="shrink-0 text-muted-foreground hover:text-danger"><X className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <EmailPreviewButton body={body} subject={subject}
                nation={contact?.nation || waDefaults.nation || null}
                recipientName={contact?.name || ""}
                informal={target === "ambassador"}
                target={target} contactId={contact?._id || null} />
              <div className="ml-auto flex gap-2">
                {allowDraft && (
                  <Button type="button" variant="secondary" onClick={() => submit("draft")} disabled={busy}
                    data-testid="composer-draft-btn"
                    className="rounded-full border border-border bg-transparent text-sm text-foreground hover:bg-brema-hover">
                    {savingDraft ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva in bozza</>}
                  </Button>
                )}
                <Button type="button" onClick={() => submit("send")} disabled={busy} data-testid="composer-send-btn"
                  className="rounded-full bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary/90">
                  {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Send className="mr-2 h-4 w-4" /> Invia email</>}
                </Button>
              </div>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Catalog & Materials picker */}
      <Dialog open={assetPickerOpen} onOpenChange={setAssetPickerOpen}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid="composer-asset-picker">
          <DialogHeader>
            <DialogTitle className="text-foreground">Catalogo &amp; Materiali</DialogTitle>
            <DialogDescription>Seleziona un file da allegare all'email.</DialogDescription>
          </DialogHeader>
          {assetsLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Caricamento…
            </div>
          ) : assets.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="composer-assets-empty">
              Nessun materiale in Catalogo. Carica prima i file dalla sezione Catalogo &amp; Materiali.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {assets.map((a) => {
                const sp = a.storage_path || a.file_path;
                const added = attachments.some((x) => x.storage_path === sp);
                return (
                  <li key={a._id || sp} data-testid="composer-asset-item"
                    className="flex items-center justify-between gap-2 rounded-lg border border-border bg-brema-bg px-3 py-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2 text-foreground">
                      <Paperclip className="h-4 w-4 shrink-0 text-brand" />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{assetName(a)}</span>
                        {(a.type || a.size) && (
                          <span className="truncate text-[11px] text-muted-foreground" data-testid="composer-asset-meta">
                            {[a.type, a.size].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </span>
                    </span>
                    <Button type="button" size="sm" disabled={pickerMode === "attachment" && attachments.some((x) => x.storage_path === sp)} onClick={() => addAsset(a)}
                      data-testid="composer-asset-add"
                      className="shrink-0 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                      {pickerMode === "wadoc" ? "Seleziona" : (added ? "Aggiunto" : "Allega")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          <Button type="button" variant="secondary" onClick={() => setAssetPickerOpen(false)} data-testid="composer-asset-close"
            className="mt-2 w-full rounded-full border border-border bg-transparent text-sm text-foreground hover:bg-brema-hover">
            Fatto
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EmailComposer;

/** CC/CCN applied to an ENTIRE bulk campaign (batch), persisted on blur. */
export function BatchCcBcc({ batch, testidPrefix = "batch" }) {
  const [cc, setCc] = useState(batch?.cc || "");
  const [bcc, setBcc] = useState(batch?.bcc || "");
  const [open, setOpen] = useState(!!(batch?.cc || batch?.bcc));
  useEffect(() => {
    setCc(batch?.cc || ""); setBcc(batch?.bcc || ""); setOpen(!!(batch?.cc || batch?.bcc));
  }, [batch?._id, batch?.cc, batch?.bcc]);
  const persist = async (nextCc = cc, nextBcc = bcc) => {
    if (!batch?._id) return;
    try { await api.patch(`/campaign-batches/${batch._id}/meta`, { cc: nextCc, bcc: nextBcc }); }
    catch (e) { toast.error("Impossibile salvare CC/CCN"); }
  };
  const clearAll = async () => {
    setCc(""); setBcc(""); setOpen(false);
    await persist("", "");
    toast.success("CC/CCN globali disattivati");
  };
  if (!open) {
    return (
      <button type="button" data-testid={`${testidPrefix}-ccbcc-toggle`} onClick={() => setOpen(true)}
        className="text-xs font-medium text-brand hover:underline">+ Aggiungi CC / CCN a tutta la campagna</button>
    );
  }
  return (
    <div className="rounded-lg border border-brand/40 bg-brand/5 p-3" data-testid={`${testidPrefix}-ccbcc`}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-foreground">CC / CCN globali della campagna</p>
        <button type="button" data-testid={`${testidPrefix}-ccbcc-remove`} onClick={clearAll}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-red-400 hover:underline">
          <X className="h-3 w-3" /> Rimuovi
        </button>
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        ⚠️ Questi indirizzi vengono messi in copia su <strong>TUTTE</strong> le email della campagna
        (ogni destinatario riceve la stessa copia). Lascia vuoto per non usarli.
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">CC — in copia (visibile a tutti)</Label>
          <Input data-testid={`${testidPrefix}-cc-input`} value={cc} onBlur={() => persist()}
            onChange={(e) => setCc(e.target.value)} placeholder="collega@brema.it" className="h-9 text-xs" />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">CCN — copia nascosta</Label>
          <Input data-testid={`${testidPrefix}-bcc-input`} value={bcc} onBlur={() => persist()}
            onChange={(e) => setBcc(e.target.value)} placeholder="archivio@brema.it" className="h-9 text-xs" />
        </div>
      </div>
    </div>
  );
}


/** Reusable email attachment control: upload custom file + pick from Catalogo & Materiali. */
export function AttachmentsInline({ attachments = [], onAdd, onRemove, testidPrefix = "att" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const assetName = (a) => a.original_filename || a.title || a.name || a.filename || "File";
  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const { data } = await api.get("/marketing-assets");
      setAssets((data.assets || []).filter((a) => a.storage_path || a.file_path));
    } catch (e) { toast.error("Impossibile caricare il Catalogo & Materiali"); }
    finally { setAssetsLoading(false); }
  };
  const openPicker = () => { setPickerOpen(true); if (!assets.length) loadAssets(); };
  const addAsset = (a) => {
    const sp = a.storage_path || a.file_path;
    if (attachments.some((x) => x.storage_path === sp)) { toast.info("Allegato già aggiunto"); return; }
    onAdd({ storage_path: sp, filename: assetName(a), content_type: a.content_type || "", size: 0, size_label: typeof a.size === "string" ? a.size : "" });
    toast.success(`Allegato: ${assetName(a)}`);
  };
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) onAdd(await uploadAttachment(f));
      toast.success("Allegato caricato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload allegato fallito"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" multiple hidden onChange={handleFiles} data-testid={`${testidPrefix}-file-input`} />
        <Button type="button" variant="secondary" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}
          data-testid={`${testidPrefix}-upload-btn`}
          className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
          {uploading ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Caricamento…</> : <><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Carica file</>}
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={openPicker}
          data-testid={`${testidPrefix}-catalog-btn`}
          className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
          <FolderOpen className="mr-1.5 h-3.5 w-3.5" /> Catalogo Brema &amp; Materiali
        </Button>
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1" data-testid={`${testidPrefix}-attachments`}>
          {attachments.map((a) => (
            <li key={a.storage_path} data-testid={`${testidPrefix}-att-item`}
              className="flex items-center justify-between gap-2 rounded-lg bg-brema-bg px-3 py-1.5 text-xs">
              <a href={fileUrl(a.storage_path)} target="_blank" rel="noopener noreferrer"
                className="flex min-w-0 items-center gap-1.5 truncate text-foreground hover:text-brand hover:underline">
                <Paperclip className="h-3 w-3 shrink-0 text-brand" />
                <span className="truncate">{a.filename}</span>
                {a.size_label && <span className="shrink-0 text-muted-foreground">· {a.size_label}</span>}
              </a>
              <button type="button" onClick={() => onRemove(a.storage_path)} data-testid={`${testidPrefix}-att-remove`}
                className="shrink-0 text-muted-foreground hover:text-danger"><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid={`${testidPrefix}-asset-picker`}>
          <DialogHeader>
            <DialogTitle className="text-foreground">Catalogo &amp; Materiali</DialogTitle>
            <DialogDescription>Seleziona il Catalogo PDF Brema o un altro materiale da allegare.</DialogDescription>
          </DialogHeader>
          {assetsLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Caricamento…</div>
          ) : assets.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid={`${testidPrefix}-assets-empty`}>
              Nessun materiale in Catalogo. Carica prima i file dalla sezione Catalogo &amp; Materiali.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {assets.map((a) => {
                const sp = a.storage_path || a.file_path;
                const added = attachments.some((x) => x.storage_path === sp);
                return (
                  <li key={a._id || sp} data-testid={`${testidPrefix}-asset-item`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border bg-brema-bg px-3 py-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2 text-foreground">
                      <Paperclip className="h-4 w-4 shrink-0 text-brand" />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{assetName(a)}</span>
                        {(a.type || a.size) && <span className="truncate text-[11px] text-muted-foreground">{[a.type, a.size].filter(Boolean).join(" · ")}</span>}
                      </span>
                    </span>
                    <Button type="button" size="sm" disabled={added} onClick={() => addAsset(a)} data-testid={`${testidPrefix}-asset-add`}
                      className="shrink-0 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                      {added ? "Aggiunto" : "Allega"}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          <Button type="button" variant="secondary" onClick={() => setPickerOpen(false)} data-testid={`${testidPrefix}-asset-close`}
            className="mt-2 w-full rounded-full border border-border bg-transparent text-sm text-foreground hover:bg-brema-hover">Fatto</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Attachments applied to an ENTIRE bulk campaign (batch), persisted immediately. */
export function BatchAttachments({ batch, testidPrefix = "batch" }) {
  const [attachments, setAttachments] = useState(batch?.attachments || []);
  useEffect(() => { setAttachments(batch?.attachments || []); }, [batch?._id]); // eslint-disable-line react-hooks/exhaustive-deps
  const persist = async (next) => {
    setAttachments(next);
    if (!batch?._id) return;
    try { await api.patch(`/campaign-batches/${batch._id}/meta`, { attachments: next }); }
    catch (e) { toast.error("Impossibile salvare gli allegati"); }
  };
  return (
    <div className="rounded-lg border border-border bg-brema-bg/50 p-3" data-testid={`${testidPrefix}-attachments-block`}>
      <p className="mb-2 text-xs font-semibold text-foreground">📎 Allegati della campagna (inviati con ogni email)</p>
      <AttachmentsInline
        attachments={attachments}
        onAdd={(r) => persist([...attachments, r])}
        onRemove={(sp) => persist(attachments.filter((a) => a.storage_path !== sp))}
        testidPrefix={testidPrefix}
      />
    </div>
  );
}
